// Layer 4 (UI) — the library tree (locked comp
// `.impeccable/mocks/decision/documents.html` B, `.tree`): documents grouped
// Procedures (by type) · Round sheets · Training material, each with its
// version and its sign-off coverage on that version; a revision in review
// carries the draft pill. Renders `Scene.transform.ts` `documentTree()`.

import { Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { CAPS, DRAFT_PILL } from './documents-ui';
import type { DocumentTreeGroup } from './Scene.transform';

type DocumentTreeProps = {
  groups: DocumentTreeGroup[];
  selectedId: string;
  onSelect: (id: string) => void;
};

export function DocumentTree({ groups, selectedId, onSelect }: DocumentTreeProps) {
  return (
    <nav aria-label="Documents" className="px-2 pb-2.5 pt-1.5">
      {groups.map((group, groupIndex) => (
        <div key={group.id}>
          <h2 className={cn(CAPS, 'px-2 pb-1', groupIndex === 0 ? 'pt-1.5' : 'pt-2.5')}>{group.label}</h2>
          {group.sections.map((section) => (
            <div key={section.id}>
              {group.showSectionLabels ? (
                <p className="px-2 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-[0.05em] text-muted opacity-85">
                  {section.label}
                </p>
              ) : null}
              <ul>
                {section.documents.map((doc) => {
                  const active = doc.id === selectedId;
                  return (
                    <li key={doc.id}>
                      <button
                        type="button"
                        aria-current={active ? 'true' : undefined}
                        data-tree-doc={doc.id}
                        onClick={() => onSelect(doc.id)}
                        className={cn(
                          'grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-px rounded-[9px] px-2 py-1.5 text-left transition-colors duration-150',
                          active
                            ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] shadow-[inset_3px_0_0_var(--color-accent-500)]'
                            : 'hover:bg-[var(--surface-1)]',
                        )}
                      >
                        <span className="truncate text-[12.5px] font-medium text-ink-primary" title={doc.title}>
                          {doc.title}
                        </span>
                        <span className="font-mono text-[11px] text-muted">{doc.version}</span>
                        <span className="col-span-2 flex flex-wrap items-center gap-1.5 text-[11px] tabular-nums text-muted" data-tree-coverage={doc.id}>
                          <span>
                            {doc.signed.current} of {doc.signed.required} signed
                          </span>
                          {doc.signed.stale > 0 ? (
                            <>
                              <span aria-hidden>·</span>
                              <span className="font-semibold text-[var(--state-blocked)]">{doc.signed.stale} stale</span>
                            </>
                          ) : null}
                          {doc.signed.overdue > 0 ? (
                            <>
                              <span aria-hidden>·</span>
                              <span className="font-semibold text-[var(--state-blocked)]">{doc.signed.overdue} overdue</span>
                            </>
                          ) : null}
                          {doc.pendingVersion ? (
                            <>
                              <span aria-hidden>·</span>
                              <Pill className={cn(DRAFT_PILL, 'px-1.5 py-0 text-[10px]')}>{doc.pendingVersion} in review</Pill>
                            </>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </nav>
  );
}
